import { FeatureNotification } from 'teacher-ui-client/helpers/feature-notification'
import { Lozenge } from 'teacher-ui-client/components/lozenge'
import { posthogEvent } from 'teacher-ui-client/modifiers/posthog'
import { ReadingEggspressReportPath, FeatureNotificationId } from 'teacher-ui-client/services/feature-notification'
import { service } from '@ember/service'
import { SideNavAccordionItem, SideNavAccordionList } from 'teacher-ui-client/components/side-nav/accordion'
import { t } from 'ember-intl'
import Component from '@glimmer/component'
import graphUnfilled from '@images/icons/graph-unfilled.svg'
import type FeaturesService from 'teacher-ui-client/services/features'
import type ImpressionService from 'teacher-ui-client/services/impression'
import type SessionService from 'teacher-ui-client/services/session'
import type SideNavService from 'teacher-ui-client/services/side-nav'
import type StandardsSet from 'teacher-ui-client/models/standards-set'

const UNSUPPORTED_REX_STANDARDS_SET_NAMES: readonly string[] = [
  'Alabama Course of Study',
  'Arizona Standards',
  'Arkansas Academic Standards',
  'Connecticut Core Standards',
  'Georgia Standards of Excellence',
  'Idaho Content Standards',
  'Illinois Learning Standards',
  'Indiana Academic Standards',
  'Iowa Academic Standards',
  'Kansas Curricular Standards',
  'Kentucky Academic Standards',
  'Louisiana Student Standards',
  'Maryland State Standards',
  'Minnesota Academic Standards',
  'Mississippi College- and Career-Readiness Standards',
  'Missouri Learning Standards',
  'New York State Next Generation Learning Standards',
  'Oklahoma Academic Standards',
  'Oregon Academic Standards',
  'South Carolina College- and Career-Ready Standards',
  'Tennessee Academic Standards',
  'Virginia Standards of Learning',
  'Washington State Learning Standards',
  'West Virginia Standards',
  'Wisconsin Academic Standards',
  'Colorado Academic Standards',
  'Alberta Program of Studies',
  'British Columbia Learning Standards',
  'Quebec Progression of Learning',
  'Newfoundland and Labrador Curriculum',
  'Nova Scotia Curriculum',
  'New Brunswick Curriculum',
  'Prince Edward Island Curriculum',
  'Saskatchewan Curriculum',
  'QLD ACiQ',
  'NT (AC V9.0)',
  'ACT (AC V9.0)',
  'SA (AC V9.0)',
  'Tasmania (AC V9.0)',
  'New Zealand',
  'Ireland PLC / MA',
  'Northern Ireland NIC',
  'Scotland CfE',
  'Wales CfW',
  'Republic of South Africa CAPS',
]

export class ReadingEggspressReportsAccordion extends Component<unknown> {
  @service impression!: ImpressionService
  @service sideNav!: SideNavService
  @service features!: FeaturesService
  @service session!: SessionService

  hasUnsupportedRexStandardsSet(standardsSet?: StandardsSet) {
    return Boolean(standardsSet && UNSUPPORTED_REX_STANDARDS_SET_NAMES.includes(standardsSet.name))
  }

  get showRexStandardsAndLessonsReports() {
    if (!this.features.isEnabled('rex_standards_and_lessons_reports')) return false
    const { standardsSet } = this.session.currentSchool
    return !this.hasUnsupportedRexStandardsSet(standardsSet)
  }

  get hasNewReadingEggspressReports() {
    return Object.values(ReadingEggspressReportPath).some((reportPath) => {
      if (
        reportPath === ReadingEggspressReportPath.StandardsPerformance ||
        reportPath === ReadingEggspressReportPath.LessonPerformance
      ) {
        return this.showRexStandardsAndLessonsReports && !this.impression.wasMade(reportPath)
      }
      return !this.impression.wasMade(reportPath)
    })
  }

  get uiScopeModels() {
    return this.sideNav.uiScopeModels ?? []
  }

  <template>
    <SideNavAccordionList
      @iconUrl={{graphUnfilled}}
      @iconAlt="Sidenav bar"
      @label={{t "sideNav.reports.title"}}
      @showNotificationDot={{this.hasNewReadingEggspressReports}}
    >
      <SideNavAccordionItem
        data-test-rex-link-dashboard
        @route="reporting.ui-scope.rex.dashboard"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="dashboard"}}
      >{{t "sideNav.reports.dashboard"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.summary.table"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="summary"}}
      >{{t "sideNav.reports.summary"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.time-on-task"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="time-on-task"}}
      >{{t "sideNav.reports.timeOnTask"}}</SideNavAccordionItem>
      {{#if this.showRexStandardsAndLessonsReports}}
        <SideNavAccordionItem
          data-test-rex-standards-performance
          @route="reporting.ui-scope.rex.standards-performance"
          @models={{this.uiScopeModels}}
          {{posthogEvent "Reporting" product="REX" page="standards-performance"}}
        >{{t "sideNav.reports.standards"}}
          {{#if
            (FeatureNotification
              FeatureNotificationId.NewReportForPath reportPath=ReadingEggspressReportPath.StandardsPerformance
            )
          }}
            <Lozenge data-test-reading-new-lozenge>{{t "new"}}</Lozenge>
          {{/if}}
        </SideNavAccordionItem>
        <SideNavAccordionItem
          data-test-rex-lesson-performance
          @route="reporting.ui-scope.rex.lesson-performance"
          @models={{this.uiScopeModels}}
          {{posthogEvent "Reporting" product="REX" page="lesson-performance"}}
        >{{t "sideNav.reports.lessonPerformance"}}
          {{#if
            (FeatureNotification
              FeatureNotificationId.NewReportForPath reportPath=ReadingEggspressReportPath.LessonPerformance
            )
          }}
            <Lozenge data-test-reading-new-lozenge>{{t "new"}}</Lozenge>
          {{/if}}
        </SideNavAccordionItem>
      {{/if}}
      <SideNavAccordionItem
        data-test-rex-link-comprehension
        @route="reporting.ui-scope.rex.course-progress.my-lessons"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="progress-my-lessons"}}
      >{{t "sideNav.reports.comprehension"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.quiz-scores"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="quiz-scores"}}
      >{{t "sideNav.reports.lessonQuizzes"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.assessment-scores"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="assessment-scores"}}
      >{{t "sideNav.reports.assessmentsCompleted"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.lexile-details"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="lexile-details"}}
      >{{t "sideNav.reports.lexileGrowth"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.course-progress.spelling"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="course-progress-spelling"}}
      >{{t "sideNav.reports.spelling"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.books-read.by-genre"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="books-read-by-genre"}}
      >{{t "sideNav.reports.booksRead"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.books-read.by-level"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="books-read-by-level"}}
      >{{t "sideNav.reports.booksReadByLevel"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.read-aloud-fluency"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="read-aloud-fluency"}}
      >{{t "sideNav.reports.readAloudFluency"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.stadium-events"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="stadium-events"}}
      >{{t "sideNav.reports.theStadium"}}</SideNavAccordionItem>
      <SideNavAccordionItem
        @route="reporting.ui-scope.rex.awards-earned"
        @models={{this.uiScopeModels}}
        {{posthogEvent "Reporting" product="REX" page="awards-earned"}}
      >{{t "sideNav.reports.trophiesEarned"}}</SideNavAccordionItem>
    </SideNavAccordionList>
  </template>
}

export default ReadingEggspressReportsAccordion
